import { motion, useTransform, type HTMLMotionProps } from 'framer-motion';
import { type ReactNode, useLayoutEffect } from 'react';
import { useMove } from 'react-aria';

import { DevicesPage, FlowsPage, HomePage, InsightsPage } from '../../../containers/dynamic';
import { getIsHomeActive } from '../../../RouteManager';
import { useHomeyPath } from '../../../RouteManager';
import { Logo } from '../../common/Logo';

import { selectedHomeyStore } from '../../../store/SelectedHomeyStore';

import { useNavContextState } from '../../../containers/NavContext';
import { useI18n } from '../../../hooks/useI18nFormatters';
import { useIsRTL } from '../../../hooks/useIsRTL';
import { useApi } from '../../../store/SelectedHomeyStore.hooks';

import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import styles from './MainNav.module.css';

import { MainNavItem } from './MainNavItem';

interface MainNavProps {
  title?: string;
  beta?: boolean;
  titleAction?: ReactNode;
  children?: ReactNode;
}

/**
 * all dynamic loading is broken for now because of layout changes, this can be
 * re-enabled when the nav is no longer part of the dynamically loaded page.
 */
export function MainNav(props: MainNavProps) {
  const { i18n } = useI18n();
  const beta = props.beta ?? false;

  const navContextState = useNavContextState();

  const { api } = useApi();
  const homeyPath = useHomeyPath();
  const isLoading = selectedHomeyStore.store((state) => state.loading);

  const prev = useTransform(navContextState.prevWidthValue, (value: number) => `${value}px`);
  const next = useTransform(navContextState.widthValue, (value: number) => `${value}px`);
  const isRTL = useIsRTL();

  const move = useMove({
    onMoveStart() {},
    onMove(event) {
      if (!isRTL && navContextState.widthValue.get() + event.deltaX < 5) {
        return;
      }

      if (isRTL && navContextState.widthValue.get() - event.deltaX < 5) {
        return;
      }

      if (isRTL) {
        navContextState.widthValue.set(navContextState.widthValue.get() - event.deltaX);
      } else {
        navContextState.widthValue.set(navContextState.widthValue.get() + event.deltaX);
      }
    },
    onMoveEnd() {
      localStorage.setItem('layout.width.sub-nav', String(navContextState.widthValue.get()));
    },
  });

  useLayoutEffect(() => {
    // The `su(9)` must match `--nav-rail-width` in MainNav.module.css. It can't
    // read that module-scoped var here because this property lives on documentElement.
    document.documentElement.style.setProperty(
      '--space-reserve-width',
      `calc(var(--width-left) + ${su(9)} + ${su(2)} * 2)`,
    );
  });

  return (
    <motion.div
      {...({
        className: styles.root,
        style: {
          '--prop-prev-width': prev,
          '--prop-next-width': next,
        },
      } as HTMLMotionProps<'div'>)}
    >
      {/*
        DOM order (logo -> main nav -> header/add -> sub-nav) drives both the
        screen-reader reading order and the keyboard tab order:
          - reading order: brand, main menu, panel title + add, sub-nav items
          - tab order: main menu items -> add-menu (titleAction) -> sub-nav items
        Logo and title are non-focusable, so only the add button and links are
        tab stops. The visual 2x2 layout is restored via CSS grid in
        MainNav.module.css.
      */}
      <div className={styles.container}>
        <div className={styles.logoWrapper}>
          <Logo isLoading={isLoading} size={'40px'} />
        </div>

        <nav className={styles.navLinkItems}>
          <MainNavItem
            exact
            to={homeyPath}
            glyphSource={glyph.system.home}
            label={i18n.messageFormatter('navigation.home')}
            dynamic={HomePage}
            isActive={getIsHomeActive}
          />
          <MainNavItem
            to={`${homeyPath}/devices`}
            glyphSource={glyph.system.devices}
            label={i18n.messageFormatter('navigation.devices')}
            dynamic={DevicesPage}
          />
          <MainNavItem
            to={`${homeyPath}/flows`}
            glyphSource={glyph.system.flow}
            label={i18n.messageFormatter('navigation.flows')}
            dynamic={FlowsPage}
          />
          <MainNavItem
            to={`${homeyPath}/insights`}
            glyphSource={glyph.system.insights}
            label={i18n.messageFormatter('navigation.insights')}
            dynamic={InsightsPage}
          />

          {api?.homey.platform === 'local' && (
            <MainNavItem
              to={`${homeyPath}/scripts`}
              glyphSource={glyph.system.homeyscript}
              label={i18n.messageFormatter('navigation.script')}
            />
          )}

          <MainNavItem
            to={`${homeyPath}/settings`}
            glyphSource={glyph.system.settings}
            label={i18n.messageFormatter('navigation.settings')}
          />
        </nav>

        <header className={styles.header}>
          {props.title != null && (
            <div className={styles.headerTitleWrapper}>
              <div className={styles.headerTitle}>
                {props.title}
                {beta && <span className={styles.beta}>Beta</span>}
              </div>
            </div>
          )}
          {props.titleAction != null && navContextState.collapsed !== true && (
            <div className={styles.headerAction}>{props.titleAction}</div>
          )}
        </header>

        <div
          className={styles.subNavWrapper}
          data-animation={getSubNavAnimation(navContextState.prevWidth, navContextState.width)}
          data-collapsed={navContextState.collapsed === true || props.children == null}
          onAnimationEnd={() => {
            //subNavigationPrevWidth = props.width;
          }}
        >
          {props.children != null && navContextState.collapsed !== true && (
            <motion.nav
              {...({
                className: styles.subNav,
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                transition: { delay: 0, ease: 'easeInOut' },
              } as HTMLMotionProps<'nav'>)}
            >
              {props.children}
              <div className={styles.handle} {...move.moveProps} />
            </motion.nav>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function getSubNavAnimation(prevWidth: number, nextWidth: number): 'none' | 'expand' | 'collapse' {
  if (nextWidth === prevWidth) {
    return 'none';
  }

  if (nextWidth > prevWidth) {
    return 'expand';
  }

  return 'collapse';
}
